import { Subscribable } from '@benzinga/subscribable';
import { Session } from '@benzinga/session';
import { CommonCalendarEvent, CommonCalendarQueryParams } from './entities';
import { CommonCalendarRequest } from './request';
import { SafePromise } from '@benzinga/safe-await';
import { CommonCalendarRequestOptions } from './internalEntities';
import { CommonCalendarEnvironmentProps } from './environment';
import { oneRequestAtATimeMultipleArgs } from '@benzinga/utils';

/**
 * Common calendar manager
 *
 * @export
 * @class CommonCalendarManager
 * @extends {Subscribable<CommonCalendarEvent<CalendarType, Entity>>}
 * @template CalendarType
 * @template Entity
 * @template Params
 */
export class CommonCalendarManager<
  CalendarType extends string,
  Entity,
  Params extends CommonCalendarQueryParams = CommonCalendarQueryParams,
> extends Subscribable<CommonCalendarEvent<CalendarType, Entity>> {
  protected session: Session;
  protected request: CommonCalendarRequest<CalendarType, Entity, Params>;
  protected calendarTypeName: string;

  private fetchCalendarDataInternal = oneRequestAtATimeMultipleArgs(async (params: Params) =>
    this.request.fetchCalendarData(params),
  );

  constructor(
    session: Session,
    environment: CommonCalendarEnvironmentProps,
    calendarTypeName: CalendarType,
    options: CommonCalendarRequestOptions<Entity, Params> = {},
  ) {
    super();
    this.session = session;
    this.calendarTypeName = calendarTypeName;
    this.request = new CommonCalendarRequest<CalendarType, Entity, Params>(
      session,
      environment,
      calendarTypeName,
      options,
    );
  }

  /**
   * Fetch calendar data, filtered by params
   *
   * @param {Params} params
   * @return {*}  {SafePromise<Entity[]>}
   * @memberof CommonCalendarManager
   */
  public fetchCalendarData = async (params: Params): SafePromise<Entity[]> => this.fetchCalendarDataInternal(params);
}
